/**
 * Compact View — High-density admin grid (15-min slots).
 *
 * Designed for studios with 5+ operators where vertical space matters.
 * Card density: initials-only chip per appointment, hover/tap reveals tooltip.
 * Drag&drop active, snap to 15min slot. Same drop semantics as Team Columns
 * (drag across staff columns + time).
 *
 * Props match TeamColumnsView for parity:
 *   appointments, staff, services, currentDate
 *   onMoveAppointment(appt, newStaffId, newTime) → Promise
 *   onClickAppointment(appt)
 *   onClickSlot(staff, time)
 *   workingHours { start, end }
 *   language
 *   filterServiceId / onFilterServiceChange
 */
import React, { useMemo, useState, useCallback } from "react";
import { format, isSameDay } from "date-fns";
import { it as itLocale, enUS } from "date-fns/locale";
import { Filter, X, Users, AlertCircle, AlertTriangle, ArrowLeftRight } from "lucide-react";

const STATUS_TONE = {
  scheduled: "from-blue-500 to-cyan-500",
  confirmed: "from-emerald-500 to-teal-500",
  pending:   "from-amber-500 to-orange-500",
  awaiting_confirmation: "from-orange-500 to-amber-500",
  completed: "from-violet-500 to-fuchsia-500",
  cancelled: "from-rose-500 to-red-500",
  no_show:   "from-slate-500 to-slate-700",
};

const SLOT_MINUTES   = 15;
const SLOT_HEIGHT_PX = 14;                       // 1 slot = 14px (high density)
const HOUR_HEIGHT_PX = (60 / SLOT_MINUTES) * SLOT_HEIGHT_PX; // 56px
const STAFF_COL_MIN_W = 96;                      // compact columns
const TIME_GUTTER_W   = 44;

function buildHourLabels(start = "08:00", end = "20:00") {
  const [sh] = start.split(":").map(Number);
  const [eh] = end.split(":").map(Number);
  const out = [];
  for (let h = sh; h <= eh; h++) out.push(`${String(h).padStart(2, "0")}:00`);
  return out;
}

function timeToY(time, firstHour) {
  if (!time) return 0;
  const [h, m] = String(time).split(":").map(Number);
  if (Number.isNaN(h)) return 0;
  return (h - firstHour) * HOUR_HEIGHT_PX + (m / 60) * HOUR_HEIGHT_PX;
}

function yToTime(y, firstHour) {
  const slots = Math.round(y / SLOT_HEIGHT_PX);
  const totalMinutes = slots * SLOT_MINUTES;
  const h = firstHour + Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function apptToMinutes(a) {
  const t = a.start_time || a.time;
  if (!t) return null;
  const [h, m] = String(t).split(":").map(Number);
  const start  = h * 60 + (m || 0);
  const dur    = parseInt(a.total_duration || a.duration || 30, 10);
  return { start, dur, end: start + dur };
}

function annotateOverlaps(items) {
  const out = items.map(it => ({ ...it, _conflict: false }));
  for (let i = 0; i < out.length; i++) {
    for (let j = i + 1; j < out.length; j++) {
      const a = apptToMinutes(out[i]);
      const b = apptToMinutes(out[j]);
      if (!a || !b) continue;
      if (a.end > b.start && b.end > a.start) {
        out[i]._conflict = true;
        out[j]._conflict = true;
      }
    }
  }
  return out;
}

const CompactView = ({
  appointments = [],
  staff = [],
  services = [],
  currentDate = new Date(),
  onMoveAppointment,
  onClickAppointment,
  onClickSlot,
  workingHours = { start: "08:00", end: "20:00" },
  language = "it",
  filterServiceId = null,
  onFilterServiceChange,
}) => {
  const hours = useMemo(() => buildHourLabels(workingHours.start, workingHours.end),
    [workingHours.start, workingHours.end]);
  const firstHour = parseInt(workingHours.start.split(":")[0], 10);
  const containerHeight = hours.length * HOUR_HEIGHT_PX + 8;

  const [dragId,    setDragId]    = useState(null);
  const [hoverDrop, setHoverDrop] = useState(null);

  const tenantHasServices = services && services.length > 0;
  const filterService = useMemo(
    () => services.find(s => s.service_id === filterServiceId),
    [services, filterServiceId]
  );

  const dayAppts = useMemo(() => {
    const target = format(currentDate, "yyyy-MM-dd");
    return (appointments || []).filter(a => (a.date || "").slice(0, 10) === target);
  }, [appointments, currentDate]);

  const byStaff = useMemo(() => {
    const m = {};
    staff.forEach(s => { m[s.staff_id] = []; });
    dayAppts.forEach(a => {
      if (!m[a.staff_id]) m[a.staff_id] = [];
      m[a.staff_id].push(a);
    });
    Object.keys(m).forEach(k => {
      m[k].sort((a, b) => {
        const ta = (a.start_time || a.time || "00:00");
        const tb = (b.start_time || b.time || "00:00");
        return ta.localeCompare(tb);
      });
      m[k] = annotateOverlaps(m[k]);
    });
    return m;
  }, [dayAppts, staff]);

  const isEligible = (member, serviceId) => {
    if (!serviceId) return true;
    const ids = member.service_ids || [];
    if (ids.length === 0) return true;
    return ids.includes(serviceId);
  };

  const handleDragStart = (e, appt) => {
    if (appt.is_blocked || appt.status === "blocked") { e.preventDefault(); return; }
    setDragId(appt.appointment_id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", appt.appointment_id); } catch { /* noop */ }
    try {
      const img = document.createElement("div");
      img.style.cssText = "position:absolute;top:-9999px;width:1px;height:1px;";
      document.body.appendChild(img);
      e.dataTransfer.setDragImage(img, 0, 0);
      setTimeout(() => document.body.removeChild(img), 0);
    } catch { /* unsupported */ }
  };
  const handleDragEnd = () => { setDragId(null); setHoverDrop(null); };

  const handleColumnDragOver = (e, staffId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const time = yToTime(Math.max(0, y), firstHour);
    setHoverDrop({ staff_id: staffId, time });
  };

  const handleDrop = (e, staffId) => {
    e.preventDefault();
    if (!dragId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const time = yToTime(Math.max(0, y), firstHour);
    const appt = dayAppts.find(a => a.appointment_id === dragId);
    if (appt && onMoveAppointment) onMoveAppointment(appt, staffId, time);
    setDragId(null);
    setHoverDrop(null);
  };

  const handleSlotClick = useCallback((e, staffObj) => {
    if (!onClickSlot) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const time = yToTime(Math.max(0, y), firstHour);
    onClickSlot(staffObj, time);
  }, [onClickSlot, firstHour]);

  if (staff.length === 0) {
    return (
      <div className="rounded-3xl border-2 border-dashed border-slate-200 dark:border-white/10 p-8 text-center" data-testid="compact-empty">
        <div className="w-10 h-10 mx-auto rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center mb-2">
          <Users className="w-5 h-5 text-white" />
        </div>
        <p className="text-sm font-bold text-slate-800 dark:text-slate-200">
          {language === "it" ? "Nessun membro del team" : "No team members"}
        </p>
      </div>
    );
  }

  const isToday = isSameDay(currentDate, new Date());
  const now = new Date();
  const nowY = (now.getHours() - firstHour) * HOUR_HEIGHT_PX + (now.getMinutes() / 60) * HOUR_HEIGHT_PX;
  const showNow = isToday && nowY > 0 && nowY < containerHeight;

  return (
    <div className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 overflow-hidden shadow-sm" data-testid="compact-view">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-3 py-2 border-b border-slate-100 dark:border-white/5 bg-gradient-to-r from-slate-50 to-white dark:from-slate-900/60 dark:to-slate-900/30">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center">
            <Users className="w-3 h-3 text-white" />
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-[0.18em] font-bold text-slate-500">Compact</p>
            <p className="text-xs font-bold text-slate-800 dark:text-slate-200">
              {format(currentDate, "EEE d MMM", { locale: language === "it" ? itLocale : enUS })} · {staff.length} {language === "it" ? "operatori" : "staff"} · slot 15min
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {tenantHasServices && onFilterServiceChange && (
            <div className="flex items-center gap-1 rounded-full bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 px-2 py-0.5" data-testid="compact-service-filter">
              <Filter className="w-3 h-3 text-slate-400" />
              <select
                className="text-[11px] font-bold bg-transparent outline-none text-slate-700 dark:text-slate-200 pr-1 max-w-[160px]"
                value={filterServiceId || ""}
                onChange={(e) => onFilterServiceChange(e.target.value || null)}
                data-testid="compact-service-filter-select"
              >
                <option value="">{language === "it" ? "Tutti i servizi" : "All services"}</option>
                {services.map(s => (
                  <option key={s.service_id} value={s.service_id}>{s.name}</option>
                ))}
              </select>
              {filterService && (
                <button type="button" onClick={() => onFilterServiceChange(null)}
                  className="text-slate-400 hover:text-slate-600" title={language === "it" ? "Cancella filtro" : "Clear"}
                  data-testid="compact-service-filter-clear">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
          <div className="hidden sm:flex items-center gap-1 text-[10px] text-slate-400">
            <ArrowLeftRight className="w-3 h-3" />
            {language === "it" ? "Drag per spostare" : "Drag to move"}
          </div>
        </div>
      </header>

      <div className="overflow-x-auto">
        <div className="flex" style={{ minWidth: TIME_GUTTER_W + staff.length * STAFF_COL_MIN_W }}>
          {/* Time gutter */}
          <div className="shrink-0 sticky left-0 bg-white dark:bg-slate-900/40 z-10 border-r border-slate-100 dark:border-white/5"
               style={{ width: TIME_GUTTER_W }}>
            <div className="h-10 border-b border-slate-100 dark:border-white/5" />
            <div className="relative" style={{ height: containerHeight }}>
              {hours.map((h, i) => (
                <div key={h}
                     className="absolute left-0 right-0 border-t border-slate-200 dark:border-white/10 text-[9px] font-mono font-bold text-slate-400 pl-1 pt-0"
                     style={{ top: i * HOUR_HEIGHT_PX }}>
                  {h}
                </div>
              ))}
            </div>
          </div>

          {/* Staff columns */}
          {staff.map(member => {
            const ap        = byStaff[member.staff_id] || [];
            const accent    = member.color || "#6366f1";
            const initial   = (member.name || "?").trim().charAt(0).toUpperCase();
            const eligible  = isEligible(member, filterServiceId);
            const dim       = filterServiceId && !eligible;
            const isUnconfigured = tenantHasServices && (!member.service_ids || member.service_ids.length === 0);
            return (
              <div key={member.staff_id} className={`shrink-0 border-r border-slate-100 dark:border-white/5 transition-opacity ${dim ? "opacity-40" : ""}`}
                   style={{ width: STAFF_COL_MIN_W, minWidth: STAFF_COL_MIN_W }}
                   data-testid={`compact-col-${member.staff_id}`}>
                {/* Column header */}
                <div className="h-10 px-2 flex items-center gap-1.5 border-b border-slate-100 dark:border-white/5 sticky top-0 bg-white dark:bg-slate-900/40 z-[5]">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-black shrink-0"
                       style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)` }}>
                    {initial}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-bold text-slate-800 dark:text-slate-200 truncate leading-tight">{member.name}</p>
                    <p className="text-[8px] uppercase tracking-wider font-bold text-slate-400 leading-tight">
                      {ap.length}{isUnconfigured && " · ⚠"}
                    </p>
                  </div>
                </div>

                {/* Column body */}
                <div
                  className={`relative ${dragId ? "bg-blue-50/40 dark:bg-blue-900/10" : ""}`}
                  style={{ height: containerHeight }}
                  onDragOver={(e) => handleColumnDragOver(e, member.staff_id)}
                  onDrop={(e) => handleDrop(e, member.staff_id)}
                  onClick={(e) => handleSlotClick(e, member)}
                  data-testid={`compact-col-body-${member.staff_id}`}
                >
                  {/* Hour grid lines (bold) */}
                  {hours.map((h, i) => (
                    <div key={h}
                         className="absolute left-0 right-0 border-t border-slate-200 dark:border-white/10"
                         style={{ top: i * HOUR_HEIGHT_PX }} />
                  ))}
                  {/* 15-min sub-grid (light) */}
                  {hours.flatMap((h, hi) => [1, 2, 3].map(q => (
                    <div key={`q-${h}-${q}`}
                         className="absolute left-0 right-0 border-t border-dashed border-slate-100/60 dark:border-white/[0.03]"
                         style={{ top: hi * HOUR_HEIGHT_PX + q * SLOT_HEIGHT_PX }} />
                  )))}

                  {/* Now line */}
                  {showNow && (
                    <div className="absolute left-0 right-0 z-[3] pointer-events-none" style={{ top: nowY }}>
                      <div className="h-px bg-gradient-to-r from-rose-500 to-pink-500" />
                      <div className="absolute -left-1 -top-1 w-2 h-2 rounded-full bg-rose-500 shadow" />
                    </div>
                  )}

                  {/* Drop ghost */}
                  {hoverDrop?.staff_id === member.staff_id && dragId && (
                    <div className="absolute left-1 right-1 rounded-md border-2 border-dashed border-blue-400 bg-blue-50/70 dark:bg-blue-900/30 pointer-events-none transition-all"
                         style={{ top: timeToY(hoverDrop.time, firstHour), height: SLOT_HEIGHT_PX * 2 }}
                         data-testid={`compact-drop-ghost-${member.staff_id}`}>
                      <div className="text-[9px] font-mono font-bold text-blue-700 dark:text-blue-300 px-1 leading-none pt-0.5">
                        {hoverDrop.time}
                      </div>
                    </div>
                  )}

                  {/* Appointments */}
                  {ap.map(appt => {
                    const startTime = appt.start_time || appt.time;
                    const startY    = timeToY(startTime, firstHour);
                    const duration  = Math.max(15, parseInt(appt.total_duration || appt.duration || 30, 10));
                    const cardH     = Math.max(SLOT_HEIGHT_PX - 1, (duration / 60) * HOUR_HEIGHT_PX - 1);
                    const tone      = STATUS_TONE[appt.status] || STATUS_TONE.scheduled;
                    const isBlock   = appt.is_blocked || appt.status === "blocked";
                    const conflict  = appt._conflict;
                    const clientInitials = (appt.client_name || "?").split(/\s+/).map(p => p.charAt(0)).join("").slice(0, 2).toUpperCase();
                    return (
                      <div
                        key={appt.appointment_id}
                        draggable={!isBlock}
                        onDragStart={(e) => handleDragStart(e, appt)}
                        onDragEnd={handleDragEnd}
                        onClick={(e) => { e.stopPropagation(); if (onClickAppointment) onClickAppointment(appt); }}
                        className={`absolute left-0.5 right-0.5 rounded-md px-1 py-0.5 cursor-pointer shadow-sm transition-all hover:shadow-md hover:-translate-y-px overflow-hidden text-white ${
                          isBlock
                            ? "bg-gradient-to-r from-slate-700 to-slate-900 opacity-90"
                            : `bg-gradient-to-r ${tone}`
                        } ${dragId === appt.appointment_id ? "opacity-50 scale-[0.96]" : ""} ${conflict ? "ring-1 ring-rose-400 ring-offset-1" : ""}`}
                        style={{ top: startY + 0.5, height: cardH, cursor: isBlock ? "default" : "grab" }}
                        data-testid={`compact-appt-${appt.appointment_id}`}
                        title={`${startTime} · ${appt.client_name || ""} · ${appt.service_name || ""}${conflict ? " · ⚠" : ""}`}
                      >
                        <div className="flex items-center justify-between leading-tight">
                          <span className="text-[8px] font-mono font-bold opacity-95">{startTime}</span>
                          {conflict && <AlertTriangle className="w-2.5 h-2.5 text-amber-100" />}
                        </div>
                        <p className="text-[9px] font-bold leading-tight truncate">
                          {cardH < 24
                            ? clientInitials
                            : (appt.client_name || (isBlock ? (appt.reason || "—") : "—"))}
                        </p>
                        {cardH >= 36 && appt.service_name && (
                          <p className="text-[8px] opacity-85 truncate leading-tight">{appt.service_name}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CompactView;
